import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { isGroupNode } from "@eudonet/opal";
import { logger } from "@/infrastructure/services/LoggerService";
import { getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import { getApiErrorStatus } from "@/shared/utils/apiErrorStatus";
import { getFilterMetadata } from "@/shared/infrastructure/api/filterMetadataApi";
import { resolveFieldLabel } from "@/shared/i18n/filterFieldLabels";
import { FILTER_FIELD_TYPE_ICON, } from "@/shared/types/filterCatalog";
/** A group no tab declares: OPAL keeps the field resolvable but never offers it in the picker. */
const HIDDEN_BUILDER_GROUP = "Hidden";
/**
 * OPHUB's `FeatureFlagFilter` errorCode when the `opqry-engine` feature flag is off
 * (the default when the config key is missing). Returned as HTTP 503.
 */
const OPQRY_ENGINE_DISABLED_ERROR_CODE = "Ophub.Feature.Disabled";
/**
 * Distinguishes "the opqry-engine flag is intentionally off" from a genuine failure.
 * The flag-off signal is a supported deployment state, not an error: OPHUB's
 * `FeatureFlagFilter` returns 503 with this errorCode, which must degrade quietly to
 * the empty/disabled builder rather than surface a toast (mirrors `useFilterCatalog`).
 */
function isFeatureDisabledResponse(e) {
    return getApiErrorStatus(e) === 503 || getApiErrorCode(e) === OPQRY_ENGINE_DISABLED_ERROR_CODE;
}
/** Lowercases only the leading character — "StartsWith" -> "startsWith", not "startswith". */
function toWireOperator(operator) {
    return (operator.charAt(0).toLowerCase() + operator.slice(1));
}
/**
 * Presentation policy — the PO operator grid (3-4-operateurs-par-type-de-champ.md).
 * Deliberately NARROWER than engine legality: set membership (in/notIn) is a
 * Liste simple concept, not offered on free text or numbers; Bool is strict
 * binary (no isNull/isNotNull). Order = display order.
 */
const OPERATOR_POLICY = {
    String: ["contains", "notContains", "eq", "neq", "startsWith", "endsWith", "isNull", "isNotNull"],
    Number: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "isNull", "isNotNull"],
    Decimal: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "isNull", "isNotNull"],
    DateTime: ["eq", "neq", "lt", "gt", "between", "lastNDays", "nextNDays", "isNull", "isNotNull"],
    // Strict binary (design D1/§4): a single `eq` operator + the Oui/Non value
    // select expresses both "est vrai" and "est faux"; `neq` would be redundant
    // ("n'est pas Oui" == "eq Non"). No isNull/isNotNull on Bool either.
    Bool: ["eq"],
    Enum: ["eq", "neq", "in", "notIn", "isNull", "isNotNull"],
    Guid: ["eq", "neq", "in", "notIn", "isNull", "isNotNull"],
};
/** True when the AST root is a group with no conditions — the "no advanced filter" state. */
function isEmptyRootGroup(ast) {
    return isGroupNode(ast) && ast.conditions.length === 0;
}
/**
 * Loads the OP-6491 filter-metadata catalog for one root entity and maps it to the OPAL
 * `AdvancedFilterBuilder` field shape, and orchestrates applying/clearing the resulting AST.
 */
export function useAdvancedFilter(entity, options = {}) {
    const { t } = useI18n();
    const excluded = new Set(options.builderExcludedPaths ?? []);
    const builderFields = ref([]);
    const fieldGroups = ref([]);
    const isLoading = ref(false);
    const hasError = ref(false);
    const isDisabled = ref(false);
    /**
     * True once load() has settled at least once (success, failure or flag-off alike). Consumers
     * that render a loading state need this in addition to isLoading: before the first load() call
     * both are false, and inferring "still pending" from builderFields.length === 0 instead would
     * latch the loading state forever on a legitimately empty catalogue.
     */
    const hasLoaded = ref(false);
    const appliedAst = ref(null);
    async function load() {
        isLoading.value = true;
        hasError.value = false;
        isDisabled.value = false;
        try {
            const metadata = await getFilterMetadata(entity);
            builderFields.value = metadata.fields.map((field) => ({
                path: field.path,
                label: resolveFieldLabel(field, t),
                type: field.type,
                group: excluded.has(field.path) ? HIDDEN_BUILDER_GROUP : field.group,
                icon: FILTER_FIELD_TYPE_ICON[field.type],
                operators: (() => {
                    const backend = new Set((metadata.operatorsPerType[field.type] ?? []).map(toWireOperator));
                    // `?? []` guards an out-of-union field type arriving from backend
                    // metadata at runtime (e.g. a referential adds File/ListMultiple before
                    // OPERATOR_POLICY knows it): the unknown field degrades to no operators
                    // instead of throwing and blanking the whole builder (mirrors line 91).
                    return (OPERATOR_POLICY[field.type] ?? []).filter((op) => backend.has(op));
                })(),
            }));
            // Only the two visible tabs are declared. RelatedDetail fields stay in
            // builderFields so an already-saved criterion still resolves its label,
            // but no declared group claims them, so the picker never offers them.
            const countOf = (group) => builderFields.value.filter((f) => f.group === group).length;
            const rootLabel = options.rootGroupLabel?.() ??
                t(entity === "contact" ? "filters.groups.contacts" : "filters.groups.organisations");
            const tabs = [{ value: "Own", label: `${rootLabel} (${countOf("Own")})` }];
            if (!options.omitEmptyRelatedTab || countOf("AggregateSnapshot") > 0) {
                tabs.push({ value: "AggregateSnapshot", label: `${t("filters.groups.related")} (${countOf("AggregateSnapshot")})` });
            }
            fieldGroups.value = tabs;
        }
        catch (e) {
            builderFields.value = [];
            fieldGroups.value = [];
            if (isFeatureDisabledResponse(e)) {
                // Expected/supported state (opqry-engine flag off) — empty catalog, no toast.
                hasError.value = false;
                isDisabled.value = true;
                logger.debug("useAdvancedFilter: opqry-engine feature flag is disabled — degrading to empty catalog", e);
            }
            else {
                hasError.value = true;
                logger.error("useAdvancedFilter: failed to load filter metadata", e);
            }
        }
        finally {
            isLoading.value = false;
            hasLoaded.value = true;
        }
    }
    function applyAst(ast) {
        appliedAst.value = isEmptyRootGroup(ast) ? null : ast;
    }
    function clear() {
        appliedAst.value = null;
    }
    return { builderFields, fieldGroups, isLoading, hasError, isDisabled, hasLoaded, load, appliedAst, applyAst, clear };
}
